import { useCallback, useMemo, useState } from "react";
import type {
  SectionInstance,
  StoreThemeJson,
  TemplateName,
  ThemeTokens,
} from "@a2z/storefront-schemas";
import { sectionRegistry } from "@a2z/storefront-schemas";

/**
 * The studio's working copy of a theme version's `StoreThemeJson`.
 *
 * Deliberately plain `useState` rather than a store: the admin edits ONE
 * version at a time, there is no autosave, no undo stack and no cross-component
 * subscription pressure. The seller editor needs `editorStore` because it
 * autosaves, mints preview tokens on a timer and drives a live postMessage
 * bridge; none of that applies here (see the preview note in
 * `docs/plans/2026-07-20-admin-theme-studio.md` §1).
 *
 * This module is the ONLY writer of the draft. Every mutation returns a new
 * object so React re-renders, and `isDirty` is derived by comparison against
 * the version as loaded — not a flag someone can forget to set.
 */

export type StudioTemplateName = TemplateName;

function cloneJson(json: StoreThemeJson): StoreThemeJson {
  return JSON.parse(JSON.stringify(json)) as StoreThemeJson;
}

/** Stable stringify for dirty-checking — key order follows insertion, and both
 *  sides of the comparison are produced by the same code path, so this is a
 *  sound equality test for our purposes without pulling in a deep-equal dep. */
function fingerprint(json: StoreThemeJson): string {
  return JSON.stringify(json);
}

export interface ThemeDraftApi {
  draft: StoreThemeJson;
  isDirty: boolean;
  activeTemplate: StudioTemplateName;
  setActiveTemplate: (name: StudioTemplateName) => void;
  /** Sections of the active template, in render order. */
  sections: SectionInstance[];
  selectedSectionId: string | null;
  select: (sectionId: string | null) => void;
  selectedSection: SectionInstance | null;

  updateSectionSettings: (
    sectionId: string,
    patch: Record<string, unknown>,
  ) => void;
  /** Patch the theme-level tokens (`settings`) — brand-wide controls. */
  updateSettings: (patch: Partial<ThemeTokens>) => void;
  toggleSectionVisibility: (sectionId: string) => void;
  moveSection: (sectionId: string, direction: "up" | "down") => void;
  removeSection: (sectionId: string) => void;
  addSection: (type: string) => void;
  /** Replace the whole document — the raw-JSON escape hatch. */
  replaceDraft: (json: StoreThemeJson) => void;
  /** Re-baseline after a successful save so `isDirty` goes false. */
  markSaved: (json: StoreThemeJson) => void;
}

export function useThemeDraft(initial: StoreThemeJson): ThemeDraftApi {
  const [baseline, setBaseline] = useState<string>(() => fingerprint(initial));
  const [draft, setDraft] = useState<StoreThemeJson>(() => cloneJson(initial));
  const [activeTemplate, setActiveTemplate] =
    useState<StudioTemplateName>("home");
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    null,
  );

  const sections = useMemo<SectionInstance[]>(
    () => draft.templates?.[activeTemplate]?.sections ?? [],
    [draft, activeTemplate],
  );

  const selectedSection = useMemo(
    () => sections.find((s) => s.id === selectedSectionId) ?? null,
    [sections, selectedSectionId],
  );

  /** Every structural edit funnels through here so the template lookup, the
   *  missing-template guard and the immutable rewrite exist in exactly one
   *  place. A section edit that bypassed it would silently write to a template
   *  object shared with the baseline and defeat the dirty check. */
  const mutateSections = useCallback(
    (fn: (sections: SectionInstance[]) => SectionInstance[]) => {
      setDraft((current) => {
        const template = current.templates?.[activeTemplate];
        if (!template) return current;
        return {
          ...current,
          templates: {
            ...current.templates,
            [activeTemplate]: {
              ...template,
              sections: fn(template.sections),
            },
          },
        };
      });
    },
    [activeTemplate],
  );

  const updateSectionSettings = useCallback(
    (sectionId: string, patch: Record<string, unknown>) => {
      mutateSections((list) =>
        list.map((s) =>
          s.id === sectionId
            ? { ...s, settings: { ...s.settings, ...patch } }
            : s,
        ),
      );
    },
    [mutateSections],
  );

  const updateSettings = useCallback((patch: Partial<ThemeTokens>) => {
    setDraft((current) => ({
      ...current,
      settings: { ...current.settings, ...patch },
    }));
  }, []);

  const toggleSectionVisibility = useCallback(
    (sectionId: string) => {
      mutateSections((list) =>
        list.map((s) =>
          s.id === sectionId
            ? // `visible` is optional and defaults to true, so the first toggle
              // on an untouched section must produce `false`, not `true`.
              { ...s, visible: s.visible === false }
            : s,
        ),
      );
    },
    [mutateSections],
  );

  const moveSection = useCallback(
    (sectionId: string, direction: "up" | "down") => {
      mutateSections((list) => {
        const idx = list.findIndex((s) => s.id === sectionId);
        if (idx === -1) return list;
        const target = direction === "up" ? idx - 1 : idx + 1;
        if (target < 0 || target >= list.length) return list;
        // Header and footer are page anchors — refuse to swap across them so a
        // reorder can never park a content section outside the page body.
        const neighbour = list[target];
        if (neighbour.type === "header" || neighbour.type === "footer") {
          return list;
        }
        const next = [...list];
        [next[idx], next[target]] = [next[target], next[idx]];
        return next;
      });
    },
    [mutateSections],
  );

  const removeSection = useCallback(
    (sectionId: string) => {
      mutateSections((list) => {
        const section = list.find((s) => s.id === sectionId);
        // The schema's `locked` flag is the authority (header/footer/catalog),
        // not the instance flag — matching the seller editor's guard.
        if (!section || sectionRegistry.sections[section.type]?.locked === true) {
          return list;
        }
        return list.filter((s) => s.id !== sectionId);
      });
      setSelectedSectionId((current) =>
        current === sectionId ? null : current,
      );
    },
    [mutateSections],
  );

  const addSection = useCallback(
    (type: string) => {
      const schema = sectionRegistry.sections[type];
      if (!schema) return;
      // Seed declared defaults, plus empty bilingual shells for i18n text so
      // the inspector has something to bind to instead of showing blanks that
      // silently create a flat string on first keystroke.
      const settings: Record<string, unknown> = {};
      for (const field of schema.settings) {
        if ("default" in field && field.default !== undefined) {
          settings[field.id] = field.default;
        } else if (
          (field.type === "text" ||
            field.type === "textarea" ||
            field.type === "richText") &&
          field.i18n
        ) {
          settings[field.id] = { ar: null, en: null };
        }
      }
      const id = `${type}-${Date.now().toString(36)}`;
      mutateSections((list) => {
        const max = schema.maxPerTemplate;
        if (
          max !== undefined &&
          list.filter((s) => s.type === type).length >= max
        ) {
          return list;
        }
        // Insert before the footer if there is one, so a new section lands in
        // the page body rather than below the footer.
        const footerIdx = list.findIndex((s) => s.type === "footer");
        const section: SectionInstance = { id, type, settings };
        if (footerIdx === -1) return [...list, section];
        return [
          ...list.slice(0, footerIdx),
          section,
          ...list.slice(footerIdx),
        ];
      });
      setSelectedSectionId(id);
    },
    [mutateSections],
  );

  const replaceDraft = useCallback((json: StoreThemeJson) => {
    setDraft(cloneJson(json));
    setSelectedSectionId(null);
  }, []);

  const markSaved = useCallback((json: StoreThemeJson) => {
    const next = cloneJson(json);
    setDraft(next);
    setBaseline(fingerprint(next));
  }, []);

  return {
    draft,
    isDirty: fingerprint(draft) !== baseline,
    activeTemplate,
    setActiveTemplate,
    sections,
    selectedSectionId,
    select: setSelectedSectionId,
    selectedSection,
    updateSectionSettings,
    updateSettings,
    toggleSectionVisibility,
    moveSection,
    removeSection,
    addSection,
    replaceDraft,
    markSaved,
  };
}
